import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Users, School, BarChart3, Award } from 'lucide-react';

const stats = [
  { icon: Users, value: 50000, suffix: '+', label: 'Students assessed' },
  { icon: School, value: 200, suffix: '+', label: 'Partner institutions' },
  { icon: BarChart3, value: 15, suffix: '+', label: 'Psychometric domains' },
  { icon: Award, value: 98, suffix: '%', label: 'Satisfaction rate' },
];

function useCountUp(target, active, duration = 1400) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    let frame;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setCount(Math.round(target * eased));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [active, target, duration]);
  return count;
}

function StatNumber({ active, value, suffix }) {
  const count = useCountUp(value, active);
  const display =
    value >= 1000 ? `${count.toLocaleString()}${suffix}` : `${count}${suffix}`;
  return (
    <div className="text-3xl lg:text-4xl font-source font-light text-white tabular-nums">
      {display}
    </div>
  );
}

const HomeStats = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });

  return (
    <section ref={ref} className="relative py-16 lg:py-20 bg-[#0F2744]">
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          background:
            'linear-gradient(105deg, transparent 20%, rgba(34,211,238,0.12) 45%, rgba(251,191,36,0.08) 55%, transparent 75%)',
        }}
        aria-hidden
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-4">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: index * 0.07 }}
              viewport={{ once: true }}
              className={`text-center px-2 ${
                index > 0 ? 'lg:border-l lg:border-white/10' : ''
              }`}
            >
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-white/10 text-cyan-300 mb-4">
                <stat.icon className="w-5 h-5" />
              </div>
              <StatNumber active={inView} value={stat.value} suffix={stat.suffix} />
              <div className="text-sm text-slate-300 font-medium tracking-wide mt-1">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HomeStats;
